import React, { useEffect, useRef, useState, useCallback, memo } from "react";
import './GameContainer.css';
import { playCollectSound, playBumpSound, playGridLevelUpSound, playGameOverSound, playRandomPopSound, playCoinSound, playGhostSound } from "../utils/sounds";
import { useAudio } from "../utils/AudioContext"; 
import { useGame } from "../utils/GameContext"; 
import { useAuth } from "../utils/AuthContext"; 
import Tile from "../Tile/Tile";
import Player from "../Player/Player";
import generateGrid from "../utils/gridGenerator";
import { getGridConfig } from "../utils/getGridSize";
import InfoBar from "../UI/InfoBar";
import TimerBar from "../UI/TimerBar";
import PowerUpTimerBar from "../UI/PowerUpTimerBar"; 
import EventNotification from "../UI/EventNotification";
import FlashlightOverlay from "../UI/FlashlightOverlay";
import GameOver from "../UI/GameOver";
import PauseMenu from "../UI/PauseMenu";
import PauseButton from "../UI/PauseButton";
import Countdown from "../UI/Countdown";
import StartOverlay from "../UI/StartOverlay";
import CollectEffect from "../UI/CollectEffect";
import ChallengeNotification from "../UI/ChallengeNotification"; 
import RollingCounter from "../UI/RollingCounter"; 
import { Coins } from 'lucide-react'; 

// --- OPTIMIZATION: GRID LAYER (STATIC ONLY) ---
const GridLayer = memo(({ grid, generationId, triggerPop, wallVariant, isHidden, tileSkinId }) => {
    return grid.map((row, r_idx) => 
        row.map((cellValue, c_idx) => {
            const key = `${generationId}-${r_idx}-${c_idx}`;
            if (cellValue === 0) return <Tile key={key} shouldPop={triggerPop} tileType="blank"/>
            if (cellValue === 1) return <Tile key={key} shouldPop={triggerPop} tileType="wall" wallVariant={wallVariant} isHidden={isHidden} skinId={tileSkinId}/>
            if (cellValue === 2) return <Tile key={key} shouldPop={triggerPop} tileType="goal"/>
            return null;
    }));
});

// --- OPTIMIZATION: ITEM LAYER (DYNAMIC ENTITIES) ---
const ItemLayer = memo(({ items, tileSize, gapSize, triggerPop }) => {
    return Object.entries(items).map(([posKey, type]) => {
        const [y, x] = posKey.split('-').map(Number);
        const style = {
            position: 'absolute',
            left: `${x * (tileSize + gapSize)}px`,
            top: `${y * (tileSize + gapSize)}px`,
            width: `${tileSize}px`,
            height: `${tileSize}px`,
            zIndex: 5 
        };
        
        return (
            <div key={posKey} style={style}>
                <Tile 
                    shouldPop={triggerPop} 
                    tileType={type === 4 ? "ghost" : "coin"} 
                />
            </div>
        );
    });
});

const MemoizedInfoBar = memo(InfoBar);
const MemoizedTimerBar = memo(TimerBar);
const MemoizedPowerUpTimerBar = memo(PowerUpTimerBar); 

function GameContainer( {startingGrid, isMenuOpen, onOpenLogin, onScoreSaved} ) {
    const { sfxMuted } = useAudio(); 
    const { gameState, setGameState, gameId, setGameId, gameMode, setGameMode, challengeData } = useGame(); 
    const { saveScore, currentUser, createChallenge, resolveChallenge, userData } = useAuth(); 

    const [grid, setGrid] = useState(startingGrid)
    const [items, setItems] = useState({}); 
    
    const [goalPos, setGoalPos] = useState({x: -1, y: -1});
    const [playerPos, setPlayerPos] = useState({x: 0, y: 0});
    const [tileSize, setTileSize] = useState(0);
    const gridWrapperRef = useRef(null)
    const [gapSize, setGapSize] = useState(0);
    const [score, setScore] = useState(0)
    
    const [collectedCoins, setCollectedCoins] = useState(0);
    const [coinAnim, setCoinAnim] = useState(false);

    const [triggerPop, setTriggerPop] = useState(0);
    const movesRef = useRef(0);
    
    const [collectEffects, setCollectEffects] = useState([]);
    
    const [deathType, setDeathType] = useState(null);
    const [isGhost, setIsGhost] = useState(false); 
    const [powerUpId, setPowerUpId] = useState(0); 
    
    const [gridOffset, setGridOffset] = useState({x: 0, y: 0});

    // --- RANDOM EVENT STATE ---
    const [notificationEvent, setNotificationEvent] = useState(null); 
    const [activeEvent, setActiveEvent] = useState(null); 
    const [isEventExiting, setIsEventExiting] = useState(false); 
    const [eventPulseDuration, setEventPulseDuration] = useState(2); 
    
    const [waitingForScore, setWaitingForScore] = useState(false);

    const warningTimerRef = useRef(null);
    const eventTimerRef = useRef(null);
    const eventExitTimerRef = useRef(null); 
    const eventLoopRef = useRef(null);
    
    // Pulse Phase Timers
    const pulseTimerRef1 = useRef(null);
    const pulseTimerRef2 = useRef(null);
    
    const eventEndTimeRef = useRef(0);
    const pausedEventRemainingRef = useRef(null); 

    const ghostTimerRef = useRef(null);

    // Lock to prevent duplicate saving
    const processedGameId = useRef(null);
    
    const [displayedMoneyBase, setDisplayedMoneyBase] = useState(0);

    const gridSize = grid.length;
    const gridConfig = getGridConfig(score);
    const targetGridSize = gridConfig.size;
    
    const wallVariant = activeEvent === 'lava_floor' ? 'lava' : 'normal';
    const isHidden = activeEvent === 'hidden_walls';

    const gridGenerationRef = useRef(0);
    const prevScoreRef = useRef(0); 
    const [duration, setDuration] = useState(5);
    const totalTimeRef = useRef(0);
    const timerRef = useRef(null);

    // --- SKINS ---
    const equippedSkin = userData?.skins?.equippedPlayer || 'default';
    const equippedTileSkin = userData?.skins?.equippedTile || 'tile_default'; 

    const stateRef = useRef({
        playerPos: {x: 0, y: 0},
        grid: startingGrid,
        items: {},
        gameState: 'ready',
        deathType: null,
        isGhost: false,
        activeEvent: null, 
        touchStart: null,
        score: 0,
        gridSize: startingGrid.length,
        wallVariant: 'normal',
        collectEffects: [],
        tileSize: 0, 
        gapSize: 0,
        isMenuOpen: false
    });

    useEffect(() => {
        stateRef.current.playerPos = playerPos;
        stateRef.current.grid = grid;
        stateRef.current.items = items; 
        stateRef.current.gameState = gameState;
        stateRef.current.deathType = deathType;
        stateRef.current.isGhost = isGhost;
        stateRef.current.activeEvent = activeEvent; 
        stateRef.current.score = score;
        stateRef.current.gridSize = gridSize;
        stateRef.current.wallVariant = wallVariant;
        stateRef.current.tileSize = tileSize;
        stateRef.current.gapSize = gapSize;
        stateRef.current.isMenuOpen = isMenuOpen;
    }, [playerPos, grid, items, gameState, deathType, isGhost, activeEvent, score, gridSize, wallVariant, tileSize, gapSize, isMenuOpen]);

    useEffect(() => {
        if (collectedCoins > 0) {
            setCoinAnim(true);
            const t = setTimeout(() => setCoinAnim(false), 200); 
            return () => clearTimeout(t);
        }
    }, [collectedCoins]);

    useEffect(() => {
        if (currentUser && userData && gameState !== 'gameover') {
            setDisplayedMoneyBase(userData.money || 0);
        } else if (!currentUser) {
            setDisplayedMoneyBase(0);
        }
    }, [userData, gameState, currentUser]);

    // --- SCORE SAVING LOGIC ---
    useEffect(() => {
        if (gameState === 'gameover') {
            if (processedGameId.current === gameId) return;

            if (gameMode === 'standard' && currentUser && userData) { 
                processedGameId.current = gameId; // LOCK
                const performSave = async () => {
                    if (saveScore) {
                        const success = await saveScore(score, collectedCoins);
                        if (success === true && onScoreSaved) onScoreSaved();
                    }
                };
                performSave();
            } 
            else if (gameMode === 'create_challenge' && challengeData && currentUser && userData) {
                processedGameId.current = gameId; // LOCK
                const performSave = async () => {
                        await createChallenge(challengeData.targetUid, challengeData.wager, score, collectedCoins);
                };
                performSave();
            }
            else if (gameMode === 'attempt_challenge' && challengeData && currentUser && userData) {
                processedGameId.current = gameId; // LOCK
                const performSave = async () => {
                    await resolveChallenge(challengeData.challengeId, challengeData, score, collectedCoins);
                };
                performSave();
            }
        }
    }, [gameState, currentUser, userData, saveScore, score, gameMode, challengeData, createChallenge, resolveChallenge, gameId, collectedCoins, onScoreSaved]);

    const prevGameMode = useRef('standard');
    useEffect(() => {
        if (prevGameMode.current !== gameMode && (gameMode === 'create_challenge' || gameMode === 'attempt_challenge')) {
            clearPowerUpTimers();
            clearEventTimers(); 
            setGameId(prevId => prevId + 1);
            setScore(0);
            setCollectedCoins(0); 
            setPlayerPos({x: 0, y: 0});
            setGrid(generateGrid(2, [0,0], 0));
            setItems({}); 
            setDuration(5);
            totalTimeRef.current = 0; 
            movesRef.current = 0; 
            setDeathType(null);
            setIsGhost(false);
            setPowerUpId(0); 
            prevScoreRef.current = 0;
            gridGenerationRef.current += 1;
            setCollectEffects([]); 
        }
        prevGameMode.current = gameMode;
    }, [gameMode, setGameId]);

    const touchStartRef = useRef(null);

    useEffect(() => {
        if(grid) {
            for(let y=0; y<grid.length; y++) {
                for(let x=0; x<grid[y].length; x++) {
                    if (grid[y][x] === 2) {
                        setGoalPos({x, y});
                        return;
                    }
                }
            }
        }
    }, [grid]);

    const handlePause = () => {
        if (deathType) return; 
        setGameState('paused');
    };

    const handleResume = () => {
        setGameState('countdown');
    };

    const onCountdownFinish = () => {
        setGameState('playing');
    };

    const handleTimeUp = useCallback(() => {
        if (stateRef.current.deathType) return;
        setDeathType('timeout');
        playGameOverSound(sfxMuted); 
        if (timerRef.current) clearInterval(timerRef.current);
        setTimeout(() => setGameState('gameover'), 1500);
    }, [sfxMuted, setGameState]);

    const clearPowerUpTimers = () => {
        if (ghostTimerRef.current) clearTimeout(ghostTimerRef.current);
    };

    const clearEventTimers = () => {
        if (warningTimerRef.current) {
            clearTimeout(warningTimerRef.current);
            warningTimerRef.current = null;
        }
        if (eventTimerRef.current) clearTimeout(eventTimerRef.current);
        if (eventExitTimerRef.current) clearTimeout(eventExitTimerRef.current);
        if (eventLoopRef.current) {
            clearTimeout(eventLoopRef.current);
            eventLoopRef.current = null;
        }
        
        // Clear Pulse Timers
        if (pulseTimerRef1.current) clearTimeout(pulseTimerRef1.current);
        if (pulseTimerRef2.current) clearTimeout(pulseTimerRef2.current);
        
        setNotificationEvent(null);
        setActiveEvent(null);
        setIsEventExiting(false);
        setWaitingForScore(false);
        eventEndTimeRef.current = 0;
        pausedEventRemainingRef.current = null;
    };

    const restartGame = () => {
        clearPowerUpTimers();
        clearEventTimers(); 
        setGameId(prevId => prevId + 1);
        setScore(0);
        setCollectedCoins(0); 
        setPlayerPos({x: 0, y: 0});
        const newGrid = generateGrid(2, [0,0], 0);
        setGrid(newGrid);
        setItems({});
        setGameState('playing');
        setDuration(5);
        totalTimeRef.current = 0; 
        movesRef.current = 0; 
        setDeathType(null);
        setIsGhost(false);
        setPowerUpId(0); 
        prevScoreRef.current = 0;
        gridGenerationRef.current += 1;
        setCollectEffects([]); 
    }

    const quitToMenu = () => {
        clearPowerUpTimers();
        clearEventTimers(); 
        setGameId(prevId => prevId + 1);
        setScore(0);
        setCollectedCoins(0); 
        setPlayerPos({x: 0, y: 0});
        const newGrid = generateGrid(2, [0,0], 0);
        setGrid(newGrid);
        setItems({});
        setGameState('ready');
        setDuration(5);
        totalTimeRef.current = 0; 
        movesRef.current = 0; 
        setDeathType(null);
        setIsGhost(false);
        setPowerUpId(0); 
        prevScoreRef.current = 0;
        gridGenerationRef.current += 1;
        setCollectEffects([]); 
        setGameMode('standard');
    }

    // --- RANDOM EVENT LOGIC ---
    const triggerEvent = (type) => {
        if (stateRef.current.gameState !== 'playing') return;
        setNotificationEvent(type);
    };

    const startActiveEvent = (type) => {
        let durationSec = 10;
        if (type === 'blackout') durationSec = 12;
        if (type === 'inverted') durationSec = 10;
        if (type === 'hidden_walls') durationSec = 10;
        if (type === 'lava_floor') durationSec = 12; 

        setActiveEvent(type);
        setIsEventExiting(false);
        setWaitingForScore(false);
        setEventPulseDuration(2); // Start slow

        const totalMs = durationSec * 1000;
        eventEndTimeRef.current = Date.now() + totalMs;
        
        scheduleEventEnd(totalMs, type);
        schedulePulsePhases(totalMs, totalMs); // Start pulses
    };

    const scheduleEventEnd = (delayMs, type) => {
        if (eventTimerRef.current) clearTimeout(eventTimerRef.current);
        eventTimerRef.current = setTimeout(() => {
            if (type === 'hidden_walls') {
                setWaitingForScore(true);
            } else {
                startEventExitSequence();
            }
        }, delayMs);
    };

    // New: Handle Pulse Phases manually
    const schedulePulsePhases = (remainingMs, totalDurationMs) => {
        // Clear old pulse timers
        if (pulseTimerRef1.current) clearTimeout(pulseTimerRef1.current);
        if (pulseTimerRef2.current) clearTimeout(pulseTimerRef2.current);

        // Only for events with effects
        if (activeEvent !== 'inverted' && activeEvent !== 'hidden_walls' && notificationEvent !== 'inverted' && notificationEvent !== 'hidden_walls') {
             // If we are just starting and the event isn't set yet (notification phase), we check notificationEvent
             const targetEvent = activeEvent || notificationEvent;
             if (targetEvent !== 'inverted' && targetEvent !== 'hidden_walls') return;
        }

        const elapsed = totalDurationMs - remainingMs;
        const phase1Time = totalDurationMs * 0.5; // 50%
        const phase2Time = totalDurationMs * 0.8; // 80%

        // Determine current state based on elapsed
        if (elapsed >= phase2Time) {
            setEventPulseDuration(0.3); // Fast
        } else if (elapsed >= phase1Time) {
            setEventPulseDuration(0.8); // Medium
            // Schedule next phase
            pulseTimerRef2.current = setTimeout(() => setEventPulseDuration(0.3), phase2Time - elapsed);
        } else {
            setEventPulseDuration(2); // Slow
            // Schedule next phases
            pulseTimerRef1.current = setTimeout(() => setEventPulseDuration(0.8), phase1Time - elapsed);
            pulseTimerRef2.current = setTimeout(() => setEventPulseDuration(0.3), phase2Time - elapsed);
        }
    };

    const startEventExitSequence = () => {
        setIsEventExiting(true); 
        setWaitingForScore(false);
    };

    const scheduleNextEvent = () => {
        if (eventLoopRef.current) clearTimeout(eventLoopRef.current);
        
        const minDelay = 2000; 
        const maxDelay = 5000;
        const randomDelay = Math.random() * (maxDelay - minDelay) + minDelay;

        eventLoopRef.current = setTimeout(() => {
            eventLoopRef.current = null;
            
            if (stateRef.current.score >= 5) {
                const rand = Math.random();
                if (rand < 0.25) triggerEvent('inverted');
                else if (rand < 0.50) triggerEvent('blackout');
                else if (rand < 0.75) triggerEvent('hidden_walls');
                else triggerEvent('lava_floor');
            } else {
                scheduleNextEvent();
            }
        }, randomDelay);
    };

    useEffect(() => {
        if (gameState === 'playing') {
            if (isEventExiting) {
                if (!eventExitTimerRef.current) {
                    eventExitTimerRef.current = setTimeout(() => {
                        setActiveEvent(null);
                        setIsEventExiting(false);
                        eventExitTimerRef.current = null;
                    }, 1000);
                }
                return;
            }

            if (activeEvent) {
                if (!waitingForScore) {
                    let remaining;
                    let totalDuration = 10000; // Default
                    if (activeEvent === 'blackout' || activeEvent === 'lava_floor') totalDuration = 12000;

                    // RESUME LOGIC
                    if (pausedEventRemainingRef.current !== null) {
                        remaining = pausedEventRemainingRef.current;
                        eventEndTimeRef.current = Date.now() + remaining;
                        pausedEventRemainingRef.current = null; 
                        
                        // Reschedule Pulse Phases based on remaining time
                        schedulePulsePhases(remaining, totalDuration);
                    } else {
                        remaining = eventEndTimeRef.current - Date.now();
                    }

                    if (remaining > 0) {
                        scheduleEventEnd(remaining, activeEvent);
                    } else {
                        if (activeEvent === 'hidden_walls') {
                            setWaitingForScore(true);
                        } else {
                            startEventExitSequence();
                        }
                    }
                }
            } 
            else if (notificationEvent) {
                if (eventLoopRef.current) { clearTimeout(eventLoopRef.current); eventLoopRef.current = null; }
                if (warningTimerRef.current) clearTimeout(warningTimerRef.current);

                warningTimerRef.current = setTimeout(() => {
                    warningTimerRef.current = null;
                    const eventType = notificationEvent;
                    setNotificationEvent(null);
                    startActiveEvent(eventType);
                }, 3000);
            }
            else if (!eventLoopRef.current) {
                scheduleNextEvent();
            }

        } else {
            // PAUSE LOGIC
            if (gameState === 'paused' && activeEvent && !waitingForScore && !isEventExiting) {
                const rem = eventEndTimeRef.current - Date.now();
                if (rem > 0) {
                    pausedEventRemainingRef.current = rem;
                }
                // Clear pulse timers so they don't fire while paused
                if (pulseTimerRef1.current) clearTimeout(pulseTimerRef1.current);
                if (pulseTimerRef2.current) clearTimeout(pulseTimerRef2.current);
            }

            if (eventLoopRef.current) { clearTimeout(eventLoopRef.current); eventLoopRef.current = null; }
            if (eventTimerRef.current) clearTimeout(eventTimerRef.current);
            if (eventExitTimerRef.current) clearTimeout(eventExitTimerRef.current);
            
            if (warningTimerRef.current) { 
                clearTimeout(warningTimerRef.current); 
                warningTimerRef.current = null; 
                setNotificationEvent(null);
            }

            if (gameState === 'gameover' || gameState === 'ready') {
                 setActiveEvent(null);
                 setNotificationEvent(null);
                 setIsEventExiting(false);
                 setWaitingForScore(false);
                 pausedEventRemainingRef.current = null;
                 if (pulseTimerRef1.current) clearTimeout(pulseTimerRef1.current);
                 if (pulseTimerRef2.current) clearTimeout(pulseTimerRef2.current);
            }
        }
        
        return () => {
             if (eventLoopRef.current) { clearTimeout(eventLoopRef.current); eventLoopRef.current = null; }
             if (eventTimerRef.current) clearTimeout(eventTimerRef.current);
             if (eventExitTimerRef.current) { clearTimeout(eventExitTimerRef.current); eventExitTimerRef.current = null; }
             if (warningTimerRef.current) { clearTimeout(warningTimerRef.current); warningTimerRef.current = null; }
        };
    }, [gameState, activeEvent, notificationEvent, isEventExiting, waitingForScore]); 

    // --- SPAWN LOGIC REFACTOR (Splits items from grid) ---
    const spawnItems = useCallback((baseGrid, size, playerX, playerY) => {
        const newItems = {};

        // 1. PowerUp Spawn (Ghost Only)
        if (!stateRef.current.isGhost) {
            const canSpawnGhost = stateRef.current.score >= 15;
            if (canSpawnGhost && Math.random() < 0.15) {
                const emptySpots = [];
                for (let y = 0; y < size; y++) {
                    for (let x = 0; x < size; x++) {
                        if (baseGrid[y][x] === 0 && (x !== playerX || y !== playerY)) emptySpots.push({x, y});
                    }
                }
                if (emptySpots.length > 0) {
                    const spot = emptySpots[Math.floor(Math.random() * emptySpots.length)];
                    newItems[`${spot.y}-${spot.x}`] = 4; // Type 4 = Ghost
                }
            }
        }

        // 2. Coin Spawn (Type 5)
        const r = Math.random();
        let numCoins = 0;
        if (r < 0.10) numCoins = 0;
        else if (r < 0.60) numCoins = 1;
        else if (r < 0.90) numCoins = 2;
        else if (r < 0.97) numCoins = 3;
        else numCoins = 4;

        if (numCoins > 0) {
            const emptySpots = [];
            for (let y = 0; y < size; y++) {
                for (let x = 0; x < size; x++) {
                    if (baseGrid[y][x] === 0 && (x !== playerX || y !== playerY) && !newItems[`${y}-${x}`]) {
                        emptySpots.push({x, y});
                    }
                }
            }
            if (emptySpots.length > 0) {
                for (let i = emptySpots.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [emptySpots[i], emptySpots[j]] = [emptySpots[j], emptySpots[i]];
                }
                const spawnCount = Math.min(numCoins, emptySpots.length);
                for(let k = 0; k < spawnCount; k++) {
                    const spot = emptySpots[k];
                    newItems[`${spot.y}-${spot.x}`] = 5; // Type 5 = Coin
                }
            }
        }
        return newItems;
    }, []);

    useEffect(() => {
        if (gameState === 'playing' && !deathType) {
            timerRef.current = setInterval(() => { totalTimeRef.current += 1; }, 1000);
        } else { clearInterval(timerRef.current); }
        return () => clearInterval(timerRef.current);
    }, [gameState, deathType]);
    
    useEffect(() => {
        if (score > 0 && score !== prevScoreRef.current) {
            prevScoreRef.current = score;

            if (activeEvent === 'hidden_walls' && waitingForScore) {
                startEventExitSequence();
            }

            const newGridSize = targetGridSize;
            let newStaticGrid;
            
            if (gridSize !== newGridSize) {
                playGridLevelUpSound(sfxMuted); 
                const relativeX = playerPos.x / gridSize;
                const relativeY = playerPos.y / gridSize;
                const newX = Math.max(0, Math.min(Math.floor(relativeX * newGridSize), newGridSize - 1));
                const newY = Math.max(0, Math.min(Math.floor(relativeY * newGridSize), newGridSize - 1));
                setPlayerPos({ x: newX, y: newY });
                gridGenerationRef.current += 1;
                newStaticGrid = generateGrid(newGridSize, [newX, newY], score);
            } else {
                playCollectSound(sfxMuted); 
                gridGenerationRef.current += 1;
                newStaticGrid = generateGrid(gridSize, [playerPos.x, playerPos.y], score);
            }
            
            const newItems = spawnItems(newStaticGrid, newGridSize || gridSize, playerPos.x, playerPos.y);
            
            setGrid(newStaticGrid);
            setItems(newItems);

            setDuration(activeEvent === 'blackout' ? 10 : 5);
            setTriggerPop(prev => prev + 1);
        }
    }, [score, targetGridSize, gridSize, sfxMuted, wallVariant, activeEvent, waitingForScore, playerPos.x, playerPos.y, spawnItems]); 
    
    useEffect(() => {
        let resizeTimeout;
        const updateTileSize = () => {
            if (gridWrapperRef.current) {
                const gridElement = gridWrapperRef.current.querySelector('.grid');
                if (!gridElement) return;
                const gapSize = parseFloat(getComputedStyle(gridElement).gap) || 0;
                setGapSize(gapSize);
                const totalGapWidth = gapSize * (gridSize - 1);
                setTileSize((gridWrapperRef.current.offsetWidth - totalGapWidth) / gridSize);
                const container = gridWrapperRef.current.parentElement; 
                if (container) {
                    const rectContainer = container.getBoundingClientRect();
                    const rectGrid = gridElement.getBoundingClientRect();
                    setGridOffset({
                        x: rectGrid.left - rectContainer.left,
                        y: rectGrid.top - rectContainer.top
                    });
                }
            }
        };
        const debouncedResize = () => { clearTimeout(resizeTimeout); resizeTimeout = setTimeout(updateTileSize, 16); };
        updateTileSize();
        window.addEventListener('resize', debouncedResize);
        return () => { window.removeEventListener('resize', debouncedResize); clearTimeout(resizeTimeout); };
    }, [gridSize]);
    
    useEffect(() => {
        const attemptMove = (inputDx, inputDy) => {
            const current = stateRef.current;
            if (current.isMenuOpen) return;
            if (current.deathType || (current.gameState !== 'playing' && current.gameState !== 'ready')) return;
            if (current.gameState === 'ready') setGameState('playing');
            let dx = inputDx, dy = inputDy;
            if (current.activeEvent === 'inverted') { dx = -inputDx; dy = -inputDy; }
            const { x, y } = current.playerPos;
            const newX = x + dx, newY = y + dy;
            
            if (newX >= 0 && newX < current.gridSize && newY >= 0 && newY < current.gridSize) {
                const staticCell = current.grid[newY][newX];
                const itemKey = `${newY}-${newX}`;
                const itemType = current.items[itemKey];

                if (staticCell === 1) { 
                    if (!current.isGhost) {
                        const isLava = current.wallVariant === 'lava';
                        if (!isLava) { playBumpSound(sfxMuted); return; }
                        else { setTimeout(() => { setDeathType('lava'); playGameOverSound(sfxMuted); setTimeout(() => setGameState('gameover'), 1500); }, 50); }
                    }
                }
                
                setPlayerPos({ x: newX, y: newY });
                playRandomPopSound(sfxMuted);
                movesRef.current += 1; 
                
                if (itemType === 4) { 
                    setIsGhost(true); 
                    setPowerUpId(prev => prev + 1); 
                    playGhostSound(sfxMuted); 
                    if (ghostTimerRef.current) clearTimeout(ghostTimerRef.current);
                    ghostTimerRef.current = setTimeout(() => { setIsGhost(false); }, 7500);
                    setItems(prev => {
                        const newItems = {...prev};
                        delete newItems[itemKey];
                        return newItems;
                    });
                } else if (itemType === 5) { 
                    setCollectedCoins(prev => prev + 1); 
                    playCoinSound(sfxMuted); 
                    const { tileSize, gapSize } = current;
                    const id = Date.now() + Math.random();
                    setCollectEffects(prev => [...prev, { id, x: newX * (tileSize + gapSize), y: newY * (tileSize + gapSize), size: tileSize, color: '#ffd700' }]);
                    setTimeout(() => setCollectEffects(prev => prev.filter(e => e.id !== id)), 800);
                    setItems(prev => {
                        const newItems = {...prev};
                        delete newItems[itemKey];
                        return newItems;
                    });
                } else if (staticCell === 2) { 
                    const { tileSize, gapSize } = current;
                    const id = Date.now() + Math.random();
                    setCollectEffects(prev => [...prev, { id, x: newX * (tileSize + gapSize), y: newY * (tileSize + gapSize), size: tileSize, color: '#00ffa3' }]);
                    setTimeout(() => setCollectEffects(prev => prev.filter(e => e.id !== id)), 800);
                    setScore(s => s + 1);
                }
            }
        };
        const handleKeyDown = (e) => {
            if (e.repeat) return;
            switch(e.key) {
                case 'ArrowUp': case 'w': case 'W': attemptMove(0, -1); break;
                case 'ArrowDown': case 's': case 'S': attemptMove(0, 1); break;
                case 'ArrowLeft': case 'a': case 'A': attemptMove(-1, 0); break;
                case 'ArrowRight': case 'd': case 'D': attemptMove(1, 0); break;
                case 'Escape': 
                    const current = stateRef.current;
                    if (current.isMenuOpen) return;
                    if (current.deathType) return;
                    if (current.gameState === 'playing') {
                        setGameState('paused');
                    } 
                    break;
                default:
                    break;
            }
        };
        const handleTouchStart = (e) => { 
            if (stateRef.current.isMenuOpen) return;
            if ((stateRef.current.gameState === 'playing' || stateRef.current.gameState === 'ready') && !stateRef.current.deathType) {
                touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; 
            }
        };
        const handleTouchMove = (e) => {
            if (!touchStartRef.current || stateRef.current.deathType) return;
            const deltaX = e.touches[0].clientX - touchStartRef.current.x, deltaY = e.touches[0].clientY - touchStartRef.current.y;
            if (Math.abs(deltaX) > 25 || Math.abs(deltaY) > 25) {
                if (Math.abs(deltaX) > Math.abs(deltaY)) attemptMove(deltaX > 0 ? 1 : -1, 0); else attemptMove(0, deltaY > 0 ? 1 : -1);
                touchStartRef.current = null;
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('touchstart', handleTouchStart, { passive: false });
        window.addEventListener('touchmove', handleTouchMove, { passive: false });
        return () => { window.removeEventListener('keydown', handleKeyDown); window.removeEventListener('touchstart', handleTouchStart); window.removeEventListener('touchmove', handleTouchMove); };
    }, [sfxMuted, setGameState]); 

    let containerClass = "game-container";
    if (activeEvent) {
        containerClass += ` event-${activeEvent}`;
        if (isEventExiting) containerClass += " exiting";
    }

    let overlayClass = "screen-effect-overlay";
    if (activeEvent === 'inverted') {
        overlayClass += ` effect-inverted`;
    } else if (activeEvent === 'hidden_walls') {
        overlayClass += ` effect-hidden-walls`;
    }
    if (isEventExiting) overlayClass += " exiting";

    const targetScore = (gameMode === 'attempt_challenge' && challengeData) 
        ? challengeData.scoreToBeat 
        : null;

    const challengeNotificationProps = (gameState === 'gameover' && gameMode === 'attempt_challenge' && challengeData) ? {
        notification: {
            type: score > challengeData.scoreToBeat ? 'win' : 'loss',
            opponentName: challengeData.senderName, 
            wager: challengeData.wager,
            score: score,
            rewardAmount: challengeData.wager 
        },
        currentMoney: userData?.money || 0,
        onDismiss: quitToMenu, 
        deductWager: true, 
        coinsCollected: collectedCoins
    } : null;

    const inGameMoneyDisplay = currentUser ? (displayedMoneyBase + collectedCoins) : collectedCoins;

    // --- FIX: PAUSE VISUAL ANIMATIONS ---
    const overlayStyle = {
        animationPlayState: gameState === 'paused' ? 'paused' : 'running'
    };

    if (activeEvent === 'inverted' || activeEvent === 'hidden_walls') {
        overlayStyle.animationDuration = `${eventPulseDuration}s`;
    }

    return (
        <div>
            <div 
                className={overlayClass} 
                style={overlayStyle}
            />

            {gameState === 'countdown' && <Countdown onCountdownFinish={onCountdownFinish} />}
            {gameState === 'paused' && <PauseMenu onResume={handleResume} onRestart={restartGame} onQuit={quitToMenu} />}
            
            {gameState === 'gameover' && (
                (gameMode === 'attempt_challenge' && challengeNotificationProps) ? (
                    <ChallengeNotification 
                        {...challengeNotificationProps} 
                    />
                ) : (
                    <GameOver 
                        score={score} 
                        collectedCoins={collectedCoins} 
                        totalTime={totalTimeRef.current} 
                        moves={movesRef.current} 
                        onRestart={restartGame} 
                        onQuit={quitToMenu}
                        gameMode={gameMode} 
                        challengeData={challengeData}
                        onOpenLogin={onOpenLogin}
                    />
                )
            )}
            
            {(gameState === 'playing' || gameState === 'paused' || gameState === 'countdown' || gameState === 'gameover') && (
                <div className={`in-game-money-display ${coinAnim ? 'pop' : ''} ${gameState === 'gameover' ? 'exiting' : ''}`}>
                    <div className="coin-icon-wrapper">
                         <Coins size={14} />
                    </div>
                    <div className="money-amount">
                        <RollingCounter value={inGameMoneyDisplay} />
                    </div>
                </div>
            )}

            <div className={`game-header ${gameState === 'ready' ? 'hidden' : ''}`}>
                <MemoizedInfoBar 
                    score={score}
                    targetScore={targetScore}
                    duration={duration}
                    handleTimeUp={handleTimeUp}
                    isRunning={gameState === 'playing' && !deathType} 
                    key={`info-${gameId}`} 
                />
                <MemoizedTimerBar 
                    duration={duration} 
                    isRunning={gameState === 'playing' && !deathType} 
                    resetKey={score} 
                    key={`timer-static-${gameId}`} 
                />
                <MemoizedPowerUpTimerBar 
                    key={`powerup-${powerUpId}`}
                    activeType={isGhost ? 'ghost' : null} 
                    duration={7.5}
                    isRunning={gameState === 'playing' && !deathType}
                />
            </div>

            <PauseButton onPause={handlePause} gameState={gameState} />
            
            <EventNotification eventType={notificationEvent} />

            <div className={containerClass}>
                <FlashlightOverlay 
                    active={activeEvent === 'blackout' && !isEventExiting}
                    playerPos={playerPos}
                    goalPos={goalPos}
                    tileSize={tileSize}
                    gapSize={gapSize}
                    gridSize={gridSize}
                    offset={gridOffset} 
                />

                <div className="grid-wrapper" ref={gridWrapperRef}>
                    <div 
                        className="grid"
                        style={{
                            gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
                            gridTemplateRows: `repeat(${gridSize}, 1fr)`,
                            position: 'relative',
                            '--tile-size': `${tileSize}px`
                        }}
                    >
                        <GridLayer 
                            grid={grid} 
                            generationId={gridGenerationRef.current} 
                            triggerPop={triggerPop} 
                            wallVariant={wallVariant} 
                            isHidden={isHidden}
                            tileSkinId={equippedTileSkin} 
                        />

                        {/* OPTIMIZATION: RENDER ITEMS SEPARATELY */}
                        {tileSize > 0 && (
                            <ItemLayer 
                                items={items}
                                tileSize={tileSize}
                                gapSize={gapSize}
                                triggerPop={triggerPop}
                            />
                        )}

                        {collectEffects.map(effect => (
                            <CollectEffect key={effect.id} x={effect.x} y={effect.y} size={effect.size} color={effect.color}/>
                        ))}
                        {tileSize > 0 && (
                            <Player 
                                gridX={playerPos.x} 
                                gridY={playerPos.y} 
                                tileSize={tileSize} 
                                gapSize={gapSize} 
                                deathType={deathType} 
                                isGhost={isGhost}
                                skinId={equippedSkin} 
                            />
                        )}
                    </div>
                </div>
                <StartOverlay isVisible={gameState === 'ready'} />
            </div>
        </div>
    );
}
export default GameContainer;